import type { ConsultantDeadlineRow } from '@shared/dto/deadline/consultant-deadline.dto';
import type { ConsultantDeadlineFormDto } from '@shared/dto/deadline/consultant-deadline-form.dto';
import type {
  ConsultantBulkCreateDeadlinesDto,
  ConsultantCreateDeadlineDto,
  ConsultantUpdateDeadlineDto,
} from '@shared/dto/deadline/consultant-deadline-write.dto';
import type { DeadlineEditFormDto } from '@shared/dto/deadline/deadline-recurrence-form.dto';
import {
  toCreateRecurrencePayload,
  toUpdateRecurrencePayload,
  toWarningDaysPayload,
} from '../../../../components/deadline-recurrence/deadline-recurrence';
import { isManagedByMyStudio } from '../deadline-row-format';

/** La persona non fa parte del DTO condiviso del form: vale solo per un cliente alla volta. */
export type ConsultantDeadlineFormValues = ConsultantDeadlineFormDto & {
  assigneeUserId?: string | null;
};

export function toBulkCreateDeadlinesDto(
  form: ConsultantDeadlineFormValues,
  uploadToken?: string,
): ConsultantBulkCreateDeadlinesDto {
  const recurrence = toCreateRecurrencePayload({
    recurrence: form.recurrence,
    dueAt: form.dueAt,
    validityMonths: form.validityMonths,
  });
  const hasDueDate = recurrence.dueAt !== undefined;
  const payload: ConsultantCreateDeadlineDto = {
    documentTypeId: form.documentTypeId,
    clientCanUpload: form.clientCanUpload,
    requestClientSignature: form.requestClientSignature,
    ...(uploadToken && form.tenantIds.length === 1 && { uploadToken }),
    ...recurrence,
    ...(form.assigneeUserId &&
      form.tenantIds.length === 1 &&
      hasDueDate && { assigneeUserId: form.assigneeUserId }),
    ...(form.titleRequired && { title: form.title?.trim(), domain: form.domain }),
    ...toWarningDaysPayload(form.recurrence, form.warningDays),
  };
  return { tenantIds: form.tenantIds, payload };
}

/** Le scelte del gestore (upload del cliente, firma richiesta) viaggiano solo per chi gestisce la riga. */
function toManagerChoicesPayload(
  values: DeadlineEditFormDto,
  row: ConsultantDeadlineRow,
): Pick<ConsultantUpdateDeadlineDto, 'clientCanUpload' | 'requestClientSignature'> {
  if (row.managedBy !== 'CONSULTANT' || !isManagedByMyStudio(row)) {
    return {};
  }
  return {
    clientCanUpload: values.clientCanUpload,
    ...(values.requestClientSignature !== undefined &&
      values.requestClientSignature !== row.requestClientSignature && {
        requestClientSignature: values.requestClientSignature,
      }),
  };
}

/** Data e validità viaggiano solo se cambiate: un salvataggio del solo titolo non tocca la ricorrenza. */
export function toConsultantUpdateDto(
  values: DeadlineEditFormDto,
  row: ConsultantDeadlineRow,
): ConsultantUpdateDeadlineDto {
  const recurrence = toUpdateRecurrencePayload({
    recurrence: values.recurrence,
    dueAt: values.dueAt,
    validityMonths: values.validityMonths,
  });
  return {
    ...(row.title !== null && { title: values.title?.trim() || undefined }),
    ...(recurrence.dueAt !== row.dueAt && { dueAt: recurrence.dueAt }),
    ...(recurrence.validityMonths !== row.validityMonths && {
      validityMonths: recurrence.validityMonths,
    }),
    ...(values.assigneeUserId !== undefined &&
      (values.assigneeUserId ?? null) !== row.assigneeUserId && {
        assigneeUserId: values.assigneeUserId,
      }),
    ...toWarningDaysPayload(values.recurrence, values.warningDays),
    ...toManagerChoicesPayload(values, row),
  };
}
